import 'lib/babelPolyfill';
import 'intersection-observer';
/* eslint-disable react/jsx-filename-extension */
import NextApp from 'next/app';
import dynamic from 'next/dynamic';
import getNextConfig from 'next/config';
import React from 'react';
import { Provider } from 'react-redux';
import withRedux from 'next-redux-wrapper';
import { makeStore } from 'redux/createStore';
import { CookiesProvider, Cookies } from 'react-cookie';

import {
  loadSocialMedia,
  setChromeless,
  disableLazyLoadAction,
  setTaboolaFeed,
  setPlatform,
} from 'redux/modules/shared';
import { setInitialTheme } from 'redux/modules/navbar';
import {
  loadMarquee,
  loadMultiMarquee,
} from 'redux/modules/marquee';

import parseQueryString from 'lib/parseQuerystring';
import VerticalContext from 'lib/ContextTypes/vertical';
import BTE from 'lib/BTE';
import { appWithTranslation } from 'lib/Locale/i18n';
import getThemesCssPath, { verticalThemeNameMap } from 'lib/getThemesCssPath';
import { isTelemundoVertical } from 'lib/vertical';

import GridGuides from 'components/GridGuides/GridGuides';

if (typeof window !== 'undefined') {
  require('picturefill'); // eslint-disable-line global-require
}

const {
  publicRuntimeConfig: {
    dev,
    ENABLE_THEME_PALETTE
  },
} = getNextConfig();

const marqueePublisher = (vertical) => {
  if (['news', 'mach', 'better', 'think', 'leftfield', 'msnbc'].includes(vertical)) {
    return ['nbcnews'];
  }
  if (vertical === 'today') {
    // Restore single marquee request on Today.
    return ['today'];
    // Used for election results.
    // return ['nbcnews', 'today'];
  }
  if (isTelemundoVertical(vertical)) {
    return ['telemundo'];
  }
  return false;
};

const defaultLayoutSetup = async ({ vertical, queryParams = null }, { dispatch }) => {
  const promises = [
    dispatch(setPlatform('ramen-nextjs')),
    dispatch(setInitialTheme(vertical)),
  ];

  // Query string params:
  const {
    chromeless,
    lazyload,
    taboolafeed,
  } = (queryParams && parseQueryString(queryParams)) || {};

  // chromeless=true - enable chromeless render
  if (chromeless && chromeless.toLowerCase() === 'true') {
    promises.push(dispatch(setChromeless()));
  }
  if (taboolafeed && taboolafeed.toLowerCase() === 'true') {
    promises.push(dispatch(setTaboolaFeed()));
  }
  // lazyload=false - disable lazyload of images/components
  //   This is used for automated visual regression testing
  if (lazyload === 'false') {
    promises.push(dispatch(disableLazyLoadAction()));
  }

  // Load social media profiles
  promises.push(
    dispatch(loadSocialMedia(vertical)),
  );

  const marquees = marqueePublisher(vertical);
  if (marquees) {
    const loadMarqueeFunc = marquees.length > 1 ? loadMultiMarquee : loadMarquee;
    promises.push(dispatch(loadMarqueeFunc(marquees.join())));
  }

  return Promise.all(promises);
};

const isBrowser = () => typeof window !== 'undefined';

const getCookies = (ctx) => {
  if (ctx && ctx.req && ctx.req.universalCookies) {
    return new Cookies(ctx.req.universalCookies);
  }
  return new Cookies();
};

class App extends NextApp {
  static async getInitialProps({ Component, ctx }) {
    const { vertical } = ctx.res.locals;

    const themesCssPath = getThemesCssPath(vertical);

    // pageProps is used in the render to provide props to the page component
    let pageProps = {};

    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps({
        ...ctx,
        themesCssPath,
      });
      // Component.WrappedComponent needed to bypass redux connect wrapper
    } else if (Component.WrappedComponent && Component.WrappedComponent.getInitialProps) {
      pageProps = await Component.WrappedComponent.getInitialProps({
        ...ctx,
        themesCssPath,
      });
    }

    await defaultLayoutSetup({ vertical, queryParams: null }, { dispatch: ctx.store.dispatch });

    if (!pageProps.namespacesRequired) {
      pageProps.namespacesRequired = [];
    }

    return {
      cookies: getCookies(),
      pageProps: {
        themesCssPath, // allow this to be overriden by placing it first
        vertical,
        ...pageProps,
      },
      Component,
      vertical,
    };
  }

  componentDidMount() {
    BTE.monitorScroll();
    BTE.monitorResize();
  }

  componentWillUnmount() {
    BTE.deregisterScroll();
    BTE.deregisterResize();
  }

  render() {
    const {
      Component,
      pageProps,
      pageProps: {
        vertical,
      },
      cookies,
      store,
    } = this.props;

    const verticalThemeName = verticalThemeNameMap[vertical] || vertical;

    // dynamically import the themes css in dev mode only
    const ThemesCssComponent = dev && vertical
      ? dynamic(() => import(`./themes/${verticalThemeName}`))
      : null;
    const ThemingPalette = (dev && ENABLE_THEME_PALETTE) 
      ? dynamic(() => import('../src/components/ThemingPalette')) 
      : null;

    return (
      <>
        <GridGuides />
        {ThemesCssComponent && (
          <ThemesCssComponent />
        )}
        <Provider store={store}>
          <CookiesProvider cookies={isBrowser() ? undefined : cookies}>
            <VerticalContext.Provider value={vertical}>
              <Component {...pageProps} />
              {ThemingPalette && (
                <ThemingPalette vertical={vertical} />
              )}
            </VerticalContext.Provider>
          </CookiesProvider>
        </Provider>
      </>
    );
  }
}

export default withRedux(makeStore)(
  appWithTranslation(App),
);
